/**
 * A referral code arrives as ?ref=CODE on any app URL (the partner's share
 * link) and must survive until the visitor signs up — possibly days later and
 * across the OAuth redirect — so it lives in localStorage with a stored-at
 * stamp. The attribution window mirrors the backend (90 days).
 */
const PENDING_REFERRAL_KEY = "pendingReferralCode";

const ATTRIBUTION_WINDOW_DAYS = 90;

const CODE_PATTERN = /^[A-Za-z0-9]{4,20}$/;

interface StoredReferral {
  code: string;
  storedAt: string;
}

/** Stashes a valid ?ref= code from the given query string; ignores everything else. */
export const captureReferralCodeFromUrl = (search: string): void => {
  const code = new URLSearchParams(search).get("ref");
  if (!code || !CODE_PATTERN.test(code)) {
    return;
  }
  try {
    const stored: StoredReferral = {
      code: code.toUpperCase(),
      storedAt: new Date().toISOString(),
    };
    localStorage.setItem(PENDING_REFERRAL_KEY, JSON.stringify(stored));
  } catch {
    // Storage unavailable (private mode etc.) — the referral is simply not tracked.
  }
};

/** The stashed code, or null when none is stored or the attribution window passed. */
export const getPendingReferralCode = (): string | null => {
  try {
    const raw = localStorage.getItem(PENDING_REFERRAL_KEY);
    if (!raw) {
      return null;
    }
    const stored = JSON.parse(raw) as StoredReferral;
    if (!stored.code || !CODE_PATTERN.test(stored.code)) {
      return null;
    }
    const ageMs = Date.now() - new Date(stored.storedAt).getTime();
    if (!Number.isFinite(ageMs) || ageMs > ATTRIBUTION_WINDOW_DAYS * 24 * 60 * 60 * 1000) {
      clearPendingReferralCode();
      return null;
    }
    return stored.code;
  } catch {
    return null;
  }
};

export const clearPendingReferralCode = (): void => {
  try {
    localStorage.removeItem(PENDING_REFERRAL_KEY);
  } catch {
    // Nothing to clean up when storage is unavailable.
  }
};
